import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import Layout from '../components/Layout';

interface ApiStats {
  endpoint: string;
  totalRequests: number;
  requestsPerSecond: number;
  requestsPerMinute: number;
  requestsPerHour: number;
  requestsPerDay: number;
  requestsPerMonth: number;
  requestsPerYear: number;
  lastUsed: string;
}

interface KeyStats {
  id: number;
  keyName: string;
  keyType: string;
  totalRequests: number;
  requestsPerSecond: number;
  requestsPerMinute: number;
  requestsPerHour: number;
  requestsPerDay: number;
  requestsPerMonth: number;
  requestsPerYear: number;
  rateLimit: number;
  isActive: boolean;
  lastUsed: string;
  topApis: ApiStats[];
}

interface SystemStats {
  totalRequests: number;
  requestsPerSecond: number;
  requestsPerMinute: number;
  requestsPerHour: number;
  requestsPerDay: number;
  requestsPerMonth: number;
  requestsPerYear: number;
  totalKeys: number;
  activeKeys: number;
  topApis: ApiStats[];
  topKeys: KeyStats[];
}

interface StatisticsData {
  systemStats: SystemStats;
  keyStats: KeyStats[];
}

const Statistics: React.FC = () => {
  const { hasPermission, user, isAuthenticated } = useAuth();

  // Debug info
  console.log('Statistics - Auth state:', { user, isAuthenticated, hasWhatsappRead: hasPermission('whatsapp', 'read') });
  const [data, setData] = useState<StatisticsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedTimeRange, setSelectedTimeRange] = useState('24h');
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedKeyDetails, setSelectedKeyDetails] = useState<KeyStats | null>(null);

  const apiRequest = async (url: string, options: RequestInit = {}) => {
    const token = localStorage.getItem('token');
    const response = await fetch(url, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        ...options.headers,
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `HTTP ${response.status}`);
    }

    return response.json();
  };

  const fetchStatistics = async () => {
    try {
      setLoading(true);
      setError(null);
      console.log('Fetching statistics...');
      console.log('Token:', localStorage.getItem('token'));
      const response = await apiRequest(`/api/whatsapp-management/statistics?timeRange=${selectedTimeRange}`);
      console.log('Statistics response:', response);
      setData(response.data);
    } catch (error: any) {
      console.error('Failed to fetch statistics:', error);
      setError(error.message || 'Failed to fetch statistics');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (hasPermission('whatsapp', 'read')) {
      fetchStatistics();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTimeRange, hasPermission]);

  const formatNumber = (num: number) => {
    if (num >= 1000000000) return (num / 1000000000).toFixed(1) + 'B';
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num.toString();
  };

  const formatRate = (rate: number) => {
    if (rate < 0) return 'Unlimited';
    return formatNumber(rate);
  };

  const handleShowDetails = (key: KeyStats) => {
    setSelectedKeyDetails(key);
    setShowDetailsModal(true);
  };

  const handleCloseDetails = () => {
    setShowDetailsModal(false);
    setSelectedKeyDetails(null);
  };

  if (!hasPermission('whatsapp', 'read')) {
    return (
      <Layout>
        <div style={{ padding: '20px', textAlign: 'center' }}>
          <h2>Access Denied</h2>
          <p>You don't have permission to view statistics.</p>
        </div>
      </Layout>
    );
  }

  if (loading) {
    return (
      <Layout>
        <div style={{ padding: '20px', textAlign: 'center' }}>
          <h2>📊 Statistics</h2>
          <p>Loading statistics...</p>
        </div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <div style={{ padding: '20px', textAlign: 'center' }}>
          <h2>📊 Statistics</h2>
          <p style={{ color: 'red' }}>Error: {error}</p>
          <button
            onClick={fetchStatistics}
            style={{
              padding: '8px 16px',
              backgroundColor: '#007bff',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              marginTop: '10px'
            }}
          >
            🔄 Retry
          </button>
        </div>
      </Layout>
    );
  }

  if (!data) {
    return (
      <Layout>
        <div style={{ padding: '20px', textAlign: 'center' }}>
          <h2>📊 Statistics</h2>
          <p>No statistics data available.</p>
          <button
            onClick={fetchStatistics}
            style={{
              padding: '8px 16px',
              backgroundColor: '#007bff',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              marginTop: '10px'
            }}
          >
            🔄 Load Statistics
          </button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div style={{ padding: '20px' }}>
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        marginBottom: '20px',
        flexWrap: 'wrap',
        gap: '10px'
      }}>
        <h2 style={{ margin: 0, color: '#333' }}>📊 API Statistics</h2>
        
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          <select
            value={selectedTimeRange}
            onChange={(e) => setSelectedTimeRange(e.target.value)}
            style={{
              padding: '8px 12px',
              border: '1px solid #ddd',
              borderRadius: '4px',
              fontSize: '14px'
            }}
          >
            <option value="1h">Last Hour</option>
            <option value="24h">Last 24 Hours</option>
            <option value="7d">Last 7 Days</option>
            <option value="30d">Last 30 Days</option>
            <option value="1y">Last Year</option>
          </select>
          
          <button
            onClick={fetchStatistics}
            style={{
              padding: '8px 16px',
              backgroundColor: '#007bff',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            🔄 Refresh
          </button>
        </div>
      </div>

      {/* System Overview */}
      <div style={{
        backgroundColor: '#f8f9fa',
        padding: '20px',
        borderRadius: '8px',
        marginBottom: '20px',
        border: '1px solid #e9ecef'
      }}>
        <h3 style={{ margin: '0 0 15px 0', color: '#495057' }}>🌐 System Overview</h3>
        
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '15px',
          marginBottom: '20px'
        }}>
          <div style={{ 
            backgroundColor: 'white', 
            padding: '15px', 
            borderRadius: '6px',
            border: '1px solid #dee2e6',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#28a745' }}>
              {formatNumber(data.systemStats.totalRequests)}
            </div>
            <div style={{ fontSize: '12px', color: '#6c757d' }}>Total Requests</div>
          </div>
          
          <div style={{ 
            backgroundColor: 'white', 
            padding: '15px', 
            borderRadius: '6px',
            border: '1px solid #dee2e6',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#007bff' }}>
              {data.systemStats.activeKeys}/{data.systemStats.totalKeys}
            </div>
            <div style={{ fontSize: '12px', color: '#6c757d' }}>Active Keys</div>
          </div>
          

        </div>

        {/* Request Rate Details */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
          gap: '10px',
          backgroundColor: 'white',
          padding: '15px',
          borderRadius: '6px',
          border: '1px solid #dee2e6'
        }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#17a2b8' }}>
              {data.systemStats.requestsPerSecond}
            </div>
            <div style={{ fontSize: '11px', color: '#6c757d' }}>Per Second</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#17a2b8' }}>
              {formatNumber(data.systemStats.requestsPerMinute)}
            </div>
            <div style={{ fontSize: '11px', color: '#6c757d' }}>Per Minute</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#28a745' }}>
              {formatNumber(data.systemStats.requestsPerHour)}
            </div>
            <div style={{ fontSize: '11px', color: '#6c757d' }}>Per Hour</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#dc3545' }}>
              {formatNumber(data.systemStats.requestsPerDay)}
            </div>
            <div style={{ fontSize: '11px', color: '#6c757d' }}>Per Day</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#fd7e14' }}>
              {formatNumber(data.systemStats.requestsPerMonth)}
            </div>
            <div style={{ fontSize: '11px', color: '#6c757d' }}>Per Month</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#6f42c1' }}>
              {formatNumber(data.systemStats.requestsPerYear)}
            </div>
            <div style={{ fontSize: '11px', color: '#6c757d' }}>Per Year</div>
          </div>
        </div>
      </div>

      {/* Top APIs */}
      <div style={{
        backgroundColor: 'white',
        padding: '20px',
        borderRadius: '8px',
        marginBottom: '20px',
        border: '1px solid #e9ecef'
      }}>
        <h3 style={{ margin: '0 0 15px 0', color: '#495057' }}>🔥 Top APIs (System)</h3>
        
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ backgroundColor: '#f8f9fa' }}>
                <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>Endpoint</th>
                <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid #dee2e6' }}>Total</th>
                <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid #dee2e6' }}>Per Hour</th>
                <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid #dee2e6' }}>Per Day</th>
                <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid #dee2e6' }}>Last Used</th>
              </tr>
            </thead>
            <tbody>
              {data.systemStats.topApis.map((api, index) => (
                <tr key={index} style={{ borderBottom: '1px solid #dee2e6' }}>
                  <td style={{ padding: '12px', fontFamily: 'monospace', fontSize: '13px' }}>
                    {api.endpoint}
                  </td>
                  <td style={{ padding: '12px', textAlign: 'center', fontWeight: 'bold' }}>
                    {formatNumber(api.totalRequests)}
                  </td>
                  <td style={{ padding: '12px', textAlign: 'center' }}>
                    {formatNumber(api.requestsPerHour)}
                  </td>
                  <td style={{ padding: '12px', textAlign: 'center' }}>
                    {formatNumber(api.requestsPerDay)}
                  </td>
                  <td style={{ padding: '12px', textAlign: 'center', fontSize: '12px', color: '#6c757d' }}>
                    {new Date(api.lastUsed).toLocaleString('vi-VN')}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* API Keys Statistics */}
      <div style={{
        backgroundColor: 'white',
        padding: '20px',
        borderRadius: '8px',
        border: '1px solid #e9ecef'
      }}>
        <h3 style={{ margin: '0 0 15px 0', color: '#495057' }}>🔑 API Keys Statistics</h3>
        
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ backgroundColor: '#f8f9fa' }}>
                <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>Key Name</th>
                <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid #dee2e6' }}>Type</th>
                <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid #dee2e6' }}>Total Requests</th>
                <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid #dee2e6' }}>Per Hour</th>
                <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid #dee2e6' }}>Rate Limit</th>
                <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid #dee2e6' }}>Status</th>
                <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid #dee2e6' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {data.keyStats.map((key) => (
                <tr key={key.id} style={{ borderBottom: '1px solid #dee2e6' }}>
                  <td style={{ padding: '12px', fontWeight: 'bold' }}>
                    {key.keyName}
                  </td>
                  <td style={{ padding: '12px', textAlign: 'center' }}>
                    <span style={{
                      padding: '4px 8px',
                      borderRadius: '12px',
                      fontSize: '12px',
                      backgroundColor: key.keyType === 'admin' ? '#dc3545' : '#28a745',
                      color: 'white'
                    }}>
                      {key.keyType}
                    </span>
                  </td>
                  <td style={{ padding: '12px', textAlign: 'center', fontWeight: 'bold' }}>
                    {formatNumber(key.totalRequests)}
                  </td>
                  <td style={{ padding: '12px', textAlign: 'center' }}>
                    {formatNumber(key.requestsPerHour)}
                  </td>
                  <td style={{ padding: '12px', textAlign: 'center' }}>
                    {formatRate(key.rateLimit)}
                  </td>
                  <td style={{ padding: '12px', textAlign: 'center' }}>
                    <span style={{
                      padding: '4px 8px',
                      borderRadius: '12px',
                      fontSize: '12px',
                      backgroundColor: key.isActive ? '#28a745' : '#6c757d',
                      color: 'white'
                    }}>
                      {key.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td style={{ padding: '12px', textAlign: 'center' }}>
                    <button
                      onClick={() => handleShowDetails(key)}
                      style={{
                        padding: '4px 8px',
                        backgroundColor: '#007bff',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '12px'
                      }}
                    >
                      Details
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      </div>

      {/* Details Modal */}
      {showDetailsModal && selectedKeyDetails && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '24px',
            maxWidth: '600px',
            width: '90%',
            maxHeight: '80vh',
            overflowY: 'auto',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)'
          }}>
            {/* Header */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '20px',
              borderBottom: '1px solid #e9ecef',
              paddingBottom: '16px'
            }}>
              <h3 style={{ margin: 0, color: '#495057' }}>
                📊 API Key Details: {selectedKeyDetails.keyName}
              </h3>
              <button
                onClick={handleCloseDetails}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: '#6c757d',
                  padding: '0',
                  width: '30px',
                  height: '30px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                ×
              </button>
            </div>

            {/* Key Info */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: '16px',
              marginBottom: '24px'
            }}>
              <div style={{
                backgroundColor: '#f8f9fa',
                padding: '16px',
                borderRadius: '6px',
                border: '1px solid #dee2e6'
              }}>
                <div style={{ fontSize: '12px', color: '#6c757d', marginBottom: '4px' }}>Key Type</div>
                <div style={{
                  fontSize: '16px',
                  fontWeight: 'bold',
                  color: selectedKeyDetails.keyType === 'admin' ? '#dc3545' : '#28a745'
                }}>
                  {selectedKeyDetails.keyType === 'admin' ? '🔑 Admin' : '👤 User'}
                </div>
              </div>

              <div style={{
                backgroundColor: '#f8f9fa',
                padding: '16px',
                borderRadius: '6px',
                border: '1px solid #dee2e6'
              }}>
                <div style={{ fontSize: '12px', color: '#6c757d', marginBottom: '4px' }}>Rate Limit</div>
                <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#495057' }}>
                  {selectedKeyDetails.rateLimit === -1 ? (
                    <span style={{ color: '#059669' }}>Vô hạn</span>
                  ) : (
                    `${formatNumber(selectedKeyDetails.rateLimit)}/h`
                  )}
                </div>
              </div>

              <div style={{
                backgroundColor: '#f8f9fa',
                padding: '16px',
                borderRadius: '6px',
                border: '1px solid #dee2e6'
              }}>
                <div style={{ fontSize: '12px', color: '#6c757d', marginBottom: '4px' }}>Status</div>
                <div style={{
                  fontSize: '16px',
                  fontWeight: 'bold',
                  color: selectedKeyDetails.isActive ? '#28a745' : '#dc3545'
                }}>
                  {selectedKeyDetails.isActive ? '✅ Active' : '❌ Inactive'}
                </div>
              </div>

              <div style={{
                backgroundColor: '#f8f9fa',
                padding: '16px',
                borderRadius: '6px',
                border: '1px solid #dee2e6'
              }}>
                <div style={{ fontSize: '12px', color: '#6c757d', marginBottom: '4px' }}>Last Used</div>
                <div style={{ fontSize: '14px', color: '#495057' }}>
                  {new Date(selectedKeyDetails.lastUsed).toLocaleString()}
                </div>
              </div>
            </div>

            {/* Usage Statistics */}
            <div style={{ marginBottom: '24px' }}>
              <h4 style={{ margin: '0 0 16px 0', color: '#495057' }}>📈 Usage Statistics</h4>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))',
                gap: '10px'
              }}>
                <div style={{ textAlign: 'center', padding: '10px', backgroundColor: '#e3f2fd', borderRadius: '6px' }}>
                  <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#1976d2' }}>
                    {formatNumber(selectedKeyDetails.totalRequests)}
                  </div>
                  <div style={{ fontSize: '10px', color: '#1976d2' }}>Total</div>
                </div>

                <div style={{ textAlign: 'center', padding: '10px', backgroundColor: '#f1f8e9', borderRadius: '6px' }}>
                  <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#689f38' }}>
                    {formatNumber(selectedKeyDetails.requestsPerSecond)}
                  </div>
                  <div style={{ fontSize: '10px', color: '#689f38' }}>Per Second</div>
                </div>

                <div style={{ textAlign: 'center', padding: '10px', backgroundColor: '#e8f5e8', borderRadius: '6px' }}>
                  <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#388e3c' }}>
                    {formatNumber(selectedKeyDetails.requestsPerMinute)}
                  </div>
                  <div style={{ fontSize: '10px', color: '#388e3c' }}>Per Minute</div>
                </div>

                <div style={{ textAlign: 'center', padding: '10px', backgroundColor: '#f3e5f5', borderRadius: '6px' }}>
                  <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#7b1fa2' }}>
                    {formatNumber(selectedKeyDetails.requestsPerHour)}
                  </div>
                  <div style={{ fontSize: '10px', color: '#7b1fa2' }}>Per Hour</div>
                </div>

                <div style={{ textAlign: 'center', padding: '10px', backgroundColor: '#e1f5fe', borderRadius: '6px' }}>
                  <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#0277bd' }}>
                    {formatNumber(selectedKeyDetails.requestsPerDay)}
                  </div>
                  <div style={{ fontSize: '10px', color: '#0277bd' }}>Per Day</div>
                </div>

                <div style={{ textAlign: 'center', padding: '10px', backgroundColor: '#fff3e0', borderRadius: '6px' }}>
                  <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#f57c00' }}>
                    {formatNumber(selectedKeyDetails.requestsPerMonth)}
                  </div>
                  <div style={{ fontSize: '10px', color: '#f57c00' }}>Per Month</div>
                </div>

                <div style={{ textAlign: 'center', padding: '10px', backgroundColor: '#fce4ec', borderRadius: '6px' }}>
                  <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#c2185b' }}>
                    {formatNumber(selectedKeyDetails.requestsPerYear)}
                  </div>
                  <div style={{ fontSize: '10px', color: '#c2185b' }}>Per Year</div>
                </div>
              </div>
            </div>

            {/* Close Button */}
            <div style={{ textAlign: 'center' }}>
              <button
                onClick={handleCloseDetails}
                style={{
                  padding: '10px 24px',
                  backgroundColor: '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default Statistics;
